/**
 * Overtime Report Mapper
 * 
 * Transforms backend DTOs (LC_*) to internal UI models
 */

import type {
  LC_OvertimeReportDataOut,
  LC_OvertimeConceptOut,
  LC_OvertimeConceptsOut,
  LC_OvertimeRecordOut,
  OvertimeConcept,
  OvertimeConcepts,
  OvertimeRecord,
  OvertimeReportResult,
} from './overtimeReport.types';

/**
 * Maps a single concept from backend format to UI format
 */
export function mapOvertimeConcept(concept: LC_OvertimeConceptOut): OvertimeConcept {
  return {
    timeFormatted: concept.hhmmss || '0:00:00',
    decimalValue: concept.decimalValue || 0,
  };
}

/**
 * Maps all concepts from backend format to UI format
 */
export function mapOvertimeConcepts(concepts: LC_OvertimeConceptsOut): OvertimeConcepts {
  return {
    overtimeT1: mapOvertimeConcept(concepts.overtimeT1),
    overtimeT2: mapOvertimeConcept(concepts.overtimeT2),
    overtimeT3: mapOvertimeConcept(concepts.overtimeT3),
    delay: mapOvertimeConcept(concepts.delay),
    absenceBetween: mapOvertimeConcept(concepts.absenceBetween),
    earlyDeparture: mapOvertimeConcept(concepts.earlyDeparture),
  };
}

/**
 * Maps a single overtime record from backend format to UI format
 */
export function mapOvertimeRecord(record: LC_OvertimeRecordOut): OvertimeRecord {
  return {
    id: record.id || '',
    type: record.type || '',
    date: record.date || '',
    schedule: record.schedule || '',
    hoursFormatted: record.hoursHHMMSS || '0:00:00',
    hoursDecimal: record.hoursDecimal || 0,
    concepts: mapOvertimeConcepts(record.concepts),
  };
}

/**
 * Maps the complete overtime report response to UI model
 */
export function mapOvertimeReportResponse(
  data: LC_OvertimeReportDataOut,
): OvertimeReportResult {
  return {
    indicators: {
      overtimeTotal: data.indicators.overtimeTotal || '0:00:00',
      overtimeTotalDecimal: data.indicators.overtimeTotalDecimal || 0,
      delaysTotal: data.indicators.delaysTotal || '0:00:00',
      delaysTotalDecimal: data.indicators.delaysTotalDecimal || 0,
    },
    records: (data.records || []).map(mapOvertimeRecord),
    totalRecords: data.totalRecords || 0,
    currentPage: data.currentPage || 1,
    totalPages: data.totalPages || 1,
    recordsPerPage: data.recordsPerPage || 10,
  };
}
